import { Checkbox, Field, InputNumber, Radio } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { AlertsPersonalSettingsStore } from './AlertsPersonalSettingsStore';
import styles from './AlertsScreen.module.scss';

type TChannelKey = 'alarmRules' | 'manual';
type TNotificationKey = 'showInfoWindows' | 'soundAlert' | 'showRecommendations';

const CHANNELS: ReadonlyArray<{ key: TChannelKey; labelKey: string }> = [
  { key: 'alarmRules', labelKey: 'settings:Alerts:columnAlarmRules' },
  { key: 'manual', labelKey: 'settings:Alerts:columnManual' },
];

const NOTIFICATION_ROWS: ReadonlyArray<{ key: TNotificationKey; labelKey: string }> = [
  { key: 'showInfoWindows', labelKey: 'settings:Alerts:showInfoWindows' },
  { key: 'soundAlert', labelKey: 'settings:Alerts:soundAlert' },
  { key: 'showRecommendations', labelKey: 'settings:Alerts:showRecommendations' },
];

const NOTIFICATION_SIZE_OPTIONS = [
  { value: 'SMALL' as const, labelKey: 'settings:Alerts:notificationSizeSmall' },
  { value: 'LARGE' as const, labelKey: 'settings:Alerts:notificationSizeLarge' },
];

const WELL_GROUPS_OPTIONS = [
  { value: 'ALL' as const, labelKey: 'settings:Alerts:wellGroupsAll' },
  { value: 'ACTIVE' as const, labelKey: 'settings:Alerts:wellGroupsActive' },
  { value: 'FAVORITES' as const, labelKey: 'settings:Alerts:wellGroupsFavorites' },
];

const MIN_RISK_OPTIONS = [
  { value: 'NO_RISK' as const, labelKey: 'settings:Alerts:riskNone' },
  { value: 'LOW_RISK' as const, labelKey: 'settings:Alerts:riskLow' },
  { value: 'MED_RISK' as const, labelKey: 'settings:Alerts:riskMed' },
  { value: 'HIGH_RISK' as const, labelKey: 'settings:Alerts:riskHigh' },
];

const FLICKER_RISK_OPTIONS = [
  { value: 'NO_RISK' as const, labelKey: 'settings:Alerts:flickerRiskNone' },
  { value: 'LOW_RISK' as const, labelKey: 'settings:Alerts:riskLow' },
  { value: 'MED_RISK' as const, labelKey: 'settings:Alerts:riskMed' },
  { value: 'HIGH_RISK' as const, labelKey: 'settings:Alerts:riskHigh' },
];

const GRID_DATA_COLS = Math.max(
  CHANNELS.length,
  NOTIFICATION_SIZE_OPTIONS.length,
  WELL_GROUPS_OPTIONS.length,
  MIN_RISK_OPTIONS.length,
  FLICKER_RISK_OPTIONS.length,
);

const GRID_STYLE = {
  gridTemplateColumns: `280px repeat(${GRID_DATA_COLS}, 130px)`,
};

function getChannelValues(
  store: AlertsPersonalSettingsStore,
  channelKey: TChannelKey,
): Record<TNotificationKey, boolean> {
  if (channelKey === 'alarmRules') {
    return {
      showInfoWindows: store.alarmRulesShowInfoWindows,
      soundAlert: store.alarmRulesSoundAlert,
      showRecommendations: store.alarmRulesShowRecommendations,
    };
  }
  return {
    showInfoWindows: store.manualShowInfoWindows,
    soundAlert: store.manualSoundAlert,
    showRecommendations: store.manualShowRecommendations,
  };
}

function getChannelSetters(
  store: AlertsPersonalSettingsStore,
  channelKey: TChannelKey,
): Record<TNotificationKey, (value: boolean) => void> {
  if (channelKey === 'alarmRules') {
    return {
      showInfoWindows: store.setAlarmRulesShowInfoWindows,
      soundAlert: store.setAlarmRulesSoundAlert,
      showRecommendations: store.setAlarmRulesShowRecommendations,
    };
  }
  return {
    showInfoWindows: store.setManualShowInfoWindows,
    soundAlert: store.setManualSoundAlert,
    showRecommendations: store.setManualShowRecommendations,
  };
}

// eslint-disable-next-line mobx/missing-observer
function EmptyCells({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} />
      ))}
    </>
  );
}

type Props = {
  store: AlertsPersonalSettingsStore;
};

export const AlertsPersonalTab = observer(function AlertsPersonalTab({ store }: Props) {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.personalGrid} style={GRID_STYLE}>
        <span className={styles.gridSectionLabel}>{t('settings:Alerts:infoWindowsTitle')}</span>
        {CHANNELS.map((ch) => (
          <span key={ch.key} className={styles.gridColumnHeader}>
            {t(ch.labelKey)}
          </span>
        ))}
        <EmptyCells count={GRID_DATA_COLS - CHANNELS.length} />

        {NOTIFICATION_ROWS.map((row) => {
          return (
            <Fragment key={row.key}>
              <span className={styles.gridRowLabel}>{t(row.labelKey)}</span>
              {CHANNELS.map((ch) => {
                const values = getChannelValues(store, ch.key);
                const setters = getChannelSetters(store, ch.key);
                return (
                  <div key={ch.key} className={styles.gridCell}>
                    <Checkbox size="s" checked={values[row.key]} onChange={setters[row.key]} />
                  </div>
                );
              })}
              <EmptyCells count={GRID_DATA_COLS - CHANNELS.length} />
            </Fragment>
          );
        })}

        <span className={styles.gridRowLabel}>{t('settings:Alerts:notificationSizeTitle')}</span>
        {NOTIFICATION_SIZE_OPTIONS.map((opt) => (
          <div key={opt.value} className={styles.gridCell}>
            <Radio
              id={`alerts-notification-size-${opt.value}`}
              size="s"
              checked={store.notificationSize === opt.value}
              value={opt.value}
              onChange={() => store.setNotificationSize(opt.value)}
            />
            <label className={styles.gridCellLabel} htmlFor={`alerts-notification-size-${opt.value}`}>
              {t(opt.labelKey)}
            </label>
          </div>
        ))}
        <EmptyCells count={GRID_DATA_COLS - NOTIFICATION_SIZE_OPTIONS.length} />

        <span className={styles.gridRowLabel}>{t('settings:Alerts:wellGroupsTitle')}</span>
        {WELL_GROUPS_OPTIONS.map((opt) => (
          <div key={opt.value} className={styles.gridCell}>
            <Radio
              id={`alerts-well-groups-${opt.value}`}
              size="s"
              checked={store.wellGroups === opt.value}
              value={opt.value}
              onChange={() => store.setWellGroups(opt.value)}
            />
            <label className={styles.gridCellLabel} htmlFor={`alerts-well-groups-${opt.value}`}>
              {t(opt.labelKey)}
            </label>
          </div>
        ))}
        <EmptyCells count={GRID_DATA_COLS - WELL_GROUPS_OPTIONS.length} />

        <span className={styles.gridRowLabel}>{t('settings:Alerts:minRiskTitle')}</span>
        {MIN_RISK_OPTIONS.map((opt) => (
          <div key={opt.value} className={styles.gridCell}>
            <Radio
              id={`alerts-min-risk-${opt.value}`}
              size="s"
              checked={store.minRisk === opt.value}
              value={opt.value}
              onChange={() => store.setMinRisk(opt.value)}
            />
            <label className={styles.gridCellLabel} htmlFor={`alerts-min-risk-${opt.value}`}>
              {t(opt.labelKey)}
            </label>
          </div>
        ))}
        <EmptyCells count={GRID_DATA_COLS - MIN_RISK_OPTIONS.length} />
      </div>

      <div className={styles.outOfGrid}>
        <div className={styles.sectionHeader}>{t('settings:Alerts:periodsTitle')}</div>
        <div className={styles.periodFields}>
          <Field label={t('settings:Alerts:periodTime')} className={styles.inputField}>
            <InputNumber
              size="s"
              value={store.periodTimeSeconds}
              min={0}
              suffix="s"
              onChange={(value) =>
                store.setPeriodTimeSeconds(value !== null && !Number.isNaN(value) ? Number(value) : null)
              }
            />
          </Field>
          <Field label={t('settings:Alerts:periodDepth')} className={styles.inputField}>
            <InputNumber
              size="s"
              value={store.periodDepth}
              min={0}
              onChange={(value) => store.setPeriodDepth(value !== null && !Number.isNaN(value) ? Number(value) : null)}
            />
          </Field>
        </div>
        <Field label={t('settings:Alerts:notificationDuration')} className={styles.inputField}>
          <InputNumber
            size="s"
            value={store.durationDisplayNotificationSeconds}
            min={0}
            suffix="s"
            onChange={(value) =>
              store.setDurationDisplayNotificationSeconds(value !== null && !Number.isNaN(value) ? Number(value) : null)
            }
          />
        </Field>
      </div>

      <div className={styles.personalGrid} style={GRID_STYLE}>
        <span className={styles.gridRowLabel}>{t('settings:Alerts:minRiskForScreenFlicker')}</span>
        {FLICKER_RISK_OPTIONS.map((opt) => (
          <div key={opt.value} className={styles.gridCell}>
            <Radio
              id={`alerts-flicker-risk-${opt.value}`}
              size="s"
              checked={store.minRiskForScreenFlicker === opt.value}
              value={opt.value}
              onChange={() => store.setMinRiskForScreenFlicker(opt.value)}
            />
            <label className={styles.gridCellLabel} htmlFor={`alerts-flicker-risk-${opt.value}`}>
              {t(opt.labelKey)}
            </label>
          </div>
        ))}
        <EmptyCells count={GRID_DATA_COLS - FLICKER_RISK_OPTIONS.length} />
      </div>

      <div className={styles.outOfGrid} style={{ paddingTop: 8 }}>
        <Field label={t('settings:Alerts:screenFlickerCount')} className={styles.inputField}>
          <InputNumber
            size="s"
            value={store.screenFlickerCount}
            min={0}
            onChange={(value) =>
              store.setScreenFlickerCount(value !== null && !Number.isNaN(value) ? Number(value) : null)
            }
          />
        </Field>
      </div>
    </>
  );
});
